import Image from 'next/image'
import styles from './EventDetails.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import Link from 'next/link'
import { Button, Heading, Paragraph } from '~elements'
import { DATE_FORMATTER, NEXT_IMAGE_QUALITY } from '~constants'
import dayjs from 'utils/dayjs'
import { useEffect, useMemo, useState } from 'react'
import { EventDetailsProps } from 'types/calendar'
import { FormattedEvent } from 'components/modules/Calendar/types/Event'

const EventDetails = ({
  disclaimer,
  eventInfo,
  onClose,
  isOpen,
  activeShowId,
  onTimeSelect,
  groupTicketsOfferText,
  premiumSuitePageLink
}: EventDetailsProps) => {
  const artistDetail = eventInfo?.artistDetail
  const events = useMemo(
    () => (eventInfo?.events ?? []).filter(e => e.artistId === artistDetail?.artistId),
    [eventInfo?.events, artistDetail?.artistId]
  )

  const currentEventDate = events[0] ? dayjs(events[0].dateTime) : null
  const formattedDate = currentEventDate?.format(DATE_FORMATTER.FULL_DATE) ?? ''
  const activeFormattedDate = currentEventDate?.format('MM/DD/YYYY') ?? ''
  const linkModule = 'calendar_sliding_panel'
  const [activeEvent, setActiveEvent] = useState<null | FormattedEvent>(null)

  const handleEscapeKey = (event: { key: string }) => {
    if (event?.key === 'Escape') {
      onClose()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [])

  useEffect(() => {
    if (events.length > 0) {
      const foundEvent = events.find(e => e.id === activeShowId) ?? null
      setActiveEvent(foundEvent)
    }
  }, [events, activeShowId])

  return (
    <>
      <div className={`${styles['selected-event']} ${isOpen ? styles['selected-event--is-open'] : ''}`}>
        <div className={styles['selected-event__image-container']}>
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: '10px' }} onClick={onClose} />
          {artistDetail?.edpPage?.slug && (
            <Link className={styles['selected-event__image-link']} href={artistDetail?.edpPage?.slug} target='_blank'>
              {artistDetail?.artistName && <Heading level={4}>{artistDetail?.artistName}</Heading>}
            </Link>
          )}
          {artistDetail?.calendarEventDetailsImage?.url && (
            <Image
              src={artistDetail?.calendarEventDetailsImage?.url}
              alt={artistDetail?.calendarEventDetailsImage?.altText || ''}
              style={{ objectFit: 'cover' }}
              fill
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>
        <div className={styles['selected-event__copy-container']}>
          <div className={styles['selected-event__date-times-container']}>
            <div className={styles['selected-event__date']}>{formattedDate}</div>
            <div className={styles['selected-event__event-times']}>
              {events
                .filter(item => item?.artistId == artistDetail?.artistId)
                .map(data => (
                  <Button
                    key={data.id}
                    color='light'
                    fill={activeShowId === data.id ? 'solid' : 'hollow'}
                    size='sm'
                    title={dayjs(data.dateTime).format('h:mm A')}
                    onClick={() => {
                      onTimeSelect(data.id)
                      setActiveEvent(data)
                    }}
                    analytics={{ skipEvent: true }}
                    isFullWidth={true}
                  />
                ))}
            </div>
          </div>
          <div className={styles['selected-event__cta-section']}>
            <div className={styles['selected-event__ctas']}>
              <Button
                isTargetBlank={true}
                disabled={false}
                isFullWidth={true}
                color='primary'
                fill='solid'
                size='lg'
                justify='left'
                url={activeEvent?.hostUrl}
                analytics={{
                  linkModule: linkModule,
                  linkPosition: '1',
                  clickTracking: `${linkModule}:${artistDetail?.artistName}:${'buy-tickets'}`,
                  eventData: {
                    ticketmasterEventId: activeEvent?.id || '',
                    eventDate: activeFormattedDate
                  }
                }}
                title={'Buy Tickets'}
              />
              {premiumSuitePageLink?.slug && (
                <Button
                  isTargetBlank={false}
                  disabled={false}
                  isFullWidth={true}
                  color='gold'
                  fill='solid'
                  size='lg'
                  url={premiumSuitePageLink?.slug || ''}
                  analytics={{
                    linkModule: linkModule,
                    linkPosition: '2',
                    clickTracking: `${linkModule}:${artistDetail?.artistName}:${'premium-suites'}`,
                    eventData: {
                      ticketmasterEventId: activeEvent?.id || '',
                      eventDate: activeFormattedDate
                    }
                  }}
                  title={'Premium Suites'}
                />
              )}
            </div>
            {artistDetail?.shouldOfferGroupTickets && (
              <div className={styles['selected-event__group-tickets-offer']}>
                {groupTicketsOfferText?.html && <Paragraph text={groupTicketsOfferText} justify='center' />}
              </div>
            )}
            {disclaimer?.html && (
              <div className={styles['selected-event__disclaimer']}>
                <Paragraph fontSize='xs' justify='center' text={disclaimer} />
              </div>
            )}
          </div>
        </div>
      </div>
      {isOpen && <div className={styles['overlay']} onClick={onClose}></div>}
    </>
  )
}

export default EventDetails
